// ============================================================================
// COURT PLACE SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { courtPlaceEndpoints, COURT_PLACE_BASE_PATH } from '../endpoints/courtPlace.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  CourtPlace,
  CourtPlaceListItem,
  CreateCourtPlaceRequest,
  UpdateCourtPlaceRequest,
  CourtPlaceSearchRequest,
} from '../types/courtPlace.types';

class CourtPlaceService {
  async getAll(params?: CourtPlaceSearchRequest): Promise<PaginatedResponse<CourtPlaceListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(COURT_PLACE_BASE_PATH, params)
      : courtPlaceEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<CourtPlace>> {
    const response = await get<ApiResponse<CourtPlace>>(courtPlaceEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<CourtPlace>> {
    const response = await get<ApiResponse<CourtPlace>>(courtPlaceEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateCourtPlaceRequest): Promise<ApiResponse<CourtPlace>> {
    const response = await post<ApiResponse<CourtPlace>>(courtPlaceEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateCourtPlaceRequest): Promise<ApiResponse<CourtPlace>> {
    const response = await put<ApiResponse<CourtPlace>>(courtPlaceEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(courtPlaceEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${courtPlaceEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : courtPlaceEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const courtPlaceService = new CourtPlaceService();
export default courtPlaceService;
